import { usePrinters, usePrintRouting, useUpsertRouting } from '@/features/print/hooks/usePrint';
import { useMenuTrees, useMenus } from '@/features/menu/hooks/useMenu';
import { loc } from '@/utils/localized';
import { useT } from '@/i18n';
import { TriStateCheckbox, type TriState } from '@/components/TriStateCheckbox';
import { deriveCategoryCell, deriveProductCell } from '@/features/print/lib/routing-view-state';
import {
  AccordionContent,
  Block,
  BlockTitle,
  BlockFooter,
  List,
  ListInput,
  ListItem,
  NavLeft,
  NavRight,
  NavTitle,
  Navbar,
  Link as F7Link,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { useEffect, useMemo, useState } from 'react';

const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

const BLOCK_COLOR = '#ff6b35';
const DIM_STYLE: React.CSSProperties = { color: 'var(--f7-text-color-muted, #8e8e93)' };
const BLOCK_TEXT_STYLE: React.CSSProperties = { color: BLOCK_COLOR, fontWeight: 500 };
const ACCENT_TEXT_STYLE: React.CSSProperties = {
  color: 'var(--f7-theme-color)',
  fontWeight: 500,
};
const MUTED_SMALL_STYLE: React.CSSProperties = {
  color: 'var(--f7-text-color-muted, #8e8e93)',
  fontSize: 12,
};

export default function PrintPageEditPage({
  f7route,
  f7router,
}: {
  f7route: { params: { pageId?: string } };
  f7router: { back: () => void };
}) {
  const t = useT();
  const { pageId } = f7route.params;
  const isEdit = Boolean(pageId) && pageId !== 'new';

  const { data: printers } = usePrinters();
  const { data: routing } = usePrintRouting();
  const { data: menus } = useMenus();
  const upsert = useUpsertRouting();

  const activeMenuIds = useMemo(
    () => (menus ?? []).filter((m) => m.isActive).map((m) => m.id),
    [menus],
  );
  const { trees } = useMenuTrees(activeMenuIds);
  const categories = useMemo(() => {
    const seen = new Set<string>();
    const out: NonNullable<(typeof trees)[number]['categories']> = [];
    for (const tree of trees) {
      for (const c of tree.categories ?? []) {
        if (!c.isActive || seen.has(c.id)) continue;
        seen.add(c.id);
        out.push(c);
      }
    }
    return out;
  }, [trees]);
  const activePrinters = useMemo(() => (printers ?? []).filter((p) => p.isActive), [printers]);

  const page = isEdit ? routing?.pages.find((p) => p.id === pageId) : null;

  const [name, setName] = useState('');
  const [printerId, setPrinterId] = useState('');
  const [selCats, setSelCats] = useState<Set<string>>(new Set());
  const [selProds, setSelProds] = useState<Set<string>>(new Set());
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (seeded || !isEdit || !page) return;
    setName(page.name);
    setPrinterId(page.printerId);
    setSelCats(new Set(page.categoryIds));
    setSelProds(new Set(page.productIds));
    setSeeded(true);
  }, [seeded, isEdit, page]);

  const productToCategory = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of categories) for (const p of c.products ?? []) m.set(p.id, c.id);
    return m;
  }, [categories]);

  const catOwners = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of routing?.pages ?? []) {
      if (isEdit && p.id === pageId) continue;
      for (const c of p.categoryIds) m.set(c, p.name);
    }
    return m;
  }, [routing, isEdit, pageId]);

  const prodOwners = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of routing?.pages ?? []) {
      if (isEdit && p.id === pageId) continue;
      for (const prod of p.productIds) m.set(prod, p.name);
    }
    return m;
  }, [routing, isEdit, pageId]);

  const catBlockedByProduct = useMemo(() => {
    const m = new Map<string, string>();
    for (const [prodId, owner] of prodOwners) {
      const catId = productToCategory.get(prodId);
      if (catId && !m.has(catId)) m.set(catId, owner);
    }
    return m;
  }, [prodOwners, productToCategory]);

  const back = () => f7router.back();

  const otherPages = () =>
    (routing?.pages ?? [])
      .filter((p) => !(isEdit && p.id === pageId))
      .map((p) => ({
        name: p.name,
        categoryIds: p.categoryIds,
        productIds: p.productIds,
        printerId: p.printerId,
      }));

  const save = () => {
    const n = name.trim();
    if (!n) return toast(t.print.stationNameRequired);
    if (!printerId) return toast(t.print.stationPrinterRequired);
    upsert.mutate(
      {
        pages: [
          ...otherPages(),
          {
            name: n,
            categoryIds: [...selCats],
            productIds: [...selProds],
            printerId,
          },
        ],
        defaultPrinterId: routing?.defaultPrinterId ?? null,
      },
      {
        onSuccess: () => {
          toast(t.print.stationSaved);
          back();
        },
        onError: (e: unknown) => toast(e instanceof Error ? e.message : t.print.saveFailed),
      },
    );
  };

  const remove = () => {
    upsert.mutate(
      { pages: otherPages(), defaultPrinterId: routing?.defaultPrinterId ?? null },
      {
        onSuccess: () => {
          toast(t.print.stationDeleted);
          back();
        },
        onError: (e: unknown) => toast(e instanceof Error ? e.message : t.print.saveFailed),
      },
    );
  };

  const clickCategory = (catId: string, prodIds: string[], state: TriState) => {
    if (state === 'blocked') return;
    if (state === 'checked') {
      setSelCats((prev) => {
        const n = new Set(prev);
        n.delete(catId);
        return n;
      });
      return;
    }
    if (state === 'partial') {
      setSelProds((prev) => {
        const n = new Set(prev);
        for (const id of prodIds) n.delete(id);
        return n;
      });
      return;
    }
    setSelCats((prev) => {
      const n = new Set(prev);
      n.add(catId);
      return n;
    });
    setSelProds((prev) => {
      const n = new Set(prev);
      for (const id of prodIds) n.delete(id);
      return n;
    });
  };

  const toggleProduct = (prodId: string) =>
    setSelProds((prev) => {
      const n = new Set(prev);
      if (n.has(prodId)) n.delete(prodId);
      else n.add(prodId);
      return n;
    });

  const loading = isEdit && !routing;
  const missing = isEdit && !loading && !page;
  const ready = !loading && !missing;
  const saving = upsert.isPending;

  return (
    <Page name="print-page-edit">
      <Navbar>
        <NavLeft>
          <F7Link onClick={back}>{t.cancel}</F7Link>
        </NavLeft>
        <NavTitle>{isEdit ? t.print.editStationTitle : t.print.addStationTitle}</NavTitle>
        <NavRight>
          <F7Link onClick={ready && !saving ? save : undefined}>{t.saveShort}</F7Link>
        </NavRight>
      </Navbar>

      {loading && (
        <Block className="text-align-center" style={{ paddingTop: 48 }}>
          <Preloader />
        </Block>
      )}

      {missing && (
        <Block strongIos strongMd outlineIos className="text-align-center">
          <p>{t.print.stationGone}</p>
        </Block>
      )}

      {ready && (
        <>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={t.print.stationNameLabel}
              type="text"
              placeholder={t.print.stationNamePlaceholder}
              value={name}
              outline={f7.theme !== 'ios'}
              floatingLabel={f7.theme !== 'ios'}
              onInput={(e) => setName((e.target as HTMLInputElement).value)}
            />
          </List>

          <BlockTitle>{t.print.stationPrinterTitle}</BlockTitle>
          {activePrinters.length === 0 ? (
            <Block strongIos strongMd outlineIos className="text-align-center text-color-gray">
              {t.print.noActivePrinters}
            </Block>
          ) : (
            <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
              {activePrinters.map((p) => (
                <ListItem
                  key={p.id}
                  radio
                  name="station-printer"
                  value={p.id}
                  title={p.name}
                  checked={printerId === p.id}
                  onChange={() => setPrinterId(p.id)}
                />
              ))}
            </List>
          )}

          <BlockTitle>{t.print.stationMenuTitle}</BlockTitle>
          <BlockFooter style={{ marginTop: -6 }}>{t.print.stationMenuFooter}</BlockFooter>
          {categories.length === 0 ? (
            <Block strongIos strongMd outlineIos className="text-align-center text-color-gray">
              {t.print.noMenuCategories}
            </Block>
          ) : (
            <List strongIos strongMd insetIos insetMd accordionList>
              {categories.map((c) => {
                const prods = (c.products ?? []).filter((p) => p.isActive);
                const prodIds = prods.map((p) => p.id);
                const selectedInCat = prodIds.filter((id) => selProds.has(id)).length;
                const cat = deriveCategoryCell({
                  ownsWholeCategory: selCats.has(c.id),
                  ownProductCount: selectedInCat,
                  otherCategoryOwner: catOwners.get(c.id),
                  otherProductOwner: catBlockedByProduct.get(c.id),
                });
                const catState = cat.state;
                const dim = catState === 'blocked';
                return (
                  <ListItem key={c.id} accordionItem data-testid={`print-cat-${c.id}`}>
                    <div slot="media" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                      <TriStateCheckbox
                        state={catState}
                        onClick={() => clickCategory(c.id, prodIds, catState)}
                        testId={`print-cat-cb-${c.id}`}
                      />
                    </div>
                    <span slot="title" style={dim ? DIM_STYLE : undefined}>
                      {loc(c.name)}
                    </span>
                    {catState === 'blocked' && cat.blockedBy && (
                      <span slot="after" style={BLOCK_TEXT_STYLE}>
                        {t.print.claimedByStation(cat.blockedBy)}
                      </span>
                    )}
                    {catState === 'partial' && (
                      <span slot="after" style={ACCENT_TEXT_STYLE}>
                        {`${selectedInCat} / ${prodIds.length}`}
                      </span>
                    )}
                    <AccordionContent>
                      {prods.length === 0 ? (
                        <Block className="text-color-gray" style={{ padding: '8px 16px' }}>
                          {t.print.noMenuProducts}
                        </Block>
                      ) : (
                        <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                          {prods.map((p) => {
                            const prod = deriveProductCell({
                              categoryChecked: catState === 'checked',
                              ownsProduct: selProds.has(p.id),
                              otherProductOwner: prodOwners.get(p.id),
                              otherCategoryOwner: catOwners.get(c.id),
                            });
                            const prodState = prod.state;
                            const locked = Boolean(prod.viaCategory) || prodState === 'blocked';
                            return (
                              <ListItem key={p.id} data-testid={`print-prod-${p.id}`}>
                                <span slot="title" style={locked ? DIM_STYLE : undefined}>
                                  {loc(p.name)}
                                </span>
                                {prod.viaCategory && (
                                  <span slot="after" style={MUTED_SMALL_STYLE}>
                                    {t.print.viaCategory}
                                  </span>
                                )}
                                {prodState === 'blocked' && prod.blockedBy && (
                                  <span slot="after" style={BLOCK_TEXT_STYLE}>
                                    {t.print.claimedByStation(prod.blockedBy)}
                                  </span>
                                )}
                                <div
                                  slot="media"
                                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                >
                                  <TriStateCheckbox
                                    state={prodState}
                                    size={20}
                                    onClick={locked ? undefined : () => toggleProduct(p.id)}
                                    testId={`print-prod-cb-${p.id}`}
                                  />
                                </div>
                              </ListItem>
                            );
                          })}
                        </List>
                      )}
                    </AccordionContent>
                  </ListItem>
                );
              })}
            </List>
          )}

          <BlockFooter>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <LegendRow state="checked" label={t.print.legendAll} />
              <LegendRow state="partial" label={t.print.legendPartial} />
              <LegendRow state="blocked" label={t.print.legendOther} />
            </div>
          </BlockFooter>

          {isEdit && (
            <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
              <ListItem
                title={t.print.stationDeleteButton}
                link="#"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  if (!saving) remove();
                }}
              >
                <i slot="media" className="f7-icons" style={{ color: '#ff3b30' }}>
                  trash
                </i>
              </ListItem>
            </List>
          )}
        </>
      )}
    </Page>
  );
}

function LegendRow({ state, label }: { state: TriState; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <TriStateCheckbox state={state} size={18} />
      <span>{label}</span>
    </span>
  );
}
